import { defineStore } from 'pinia'
import { me } from '@/api/user'
import { useAuthStore } from '@/stores/auth'

// 当前用户的数据范围（前端表达层）
// 统一从 /api/user/me 取一次，供本页与后续 R3 题库、R5 看板复用，避免各模块重复取数。
// 注意：此为体验层锁定，真实隔离须由后端按角色注入（接口清单 §6.3）。
const WRITE_ROLES = ['HEAD_TEACHING', 'HEAD_PREP', 'ADMIN']

export const useScopeStore = defineStore('scope', {
  state: () => ({
    loaded: false,
    username: '',
    userId: null,
    roles: [],
    mySubjectId: null, // 教师任教学科，用于锁定本学科
    rawGradeIds: [],    // 后端回写的绑定年段集合（原始，不区分角色）
    superAdmin: false,  // 超级管理员(登录名 admin)：系统内唯一具备破坏性删除权的账号
    // 当前用户权限快照：module -> [action...]，由 /api/user/me 回写（统一权限模型）。
    // 替代硬编码角色码判定按钮显隐，配置由超级管理员在「权限管理」页维护。
    permissions: {}
  }),
  getters: {
    // 删除类按钮统一以此判定显隐；后端 /api/user/me 回显 isSuperAdmin，前端不自行拼登录名。
    // 约定：新增/编辑/导入等非破坏性管理按钮仍用 canWrite 或角色码，不要误用本标识。
    isSuperAdmin: (state) => state.superAdmin,
    // 以“当前激活角色”为准（方案 A：UI 视图切换）。
    // 多角色用户切到教师即只读，切到教学组长/备课组长/教务主任即可写；
    // 服务端仍按角色并集授权，前端仅切换工作视图与写按钮显隐。
    canWrite: () => WRITE_ROLES.includes(useAuthStore().currentRole),
    isReadOnly: () => !WRITE_ROLES.includes(useAuthStore().currentRole),
    isTeacher: () => useAuthStore().currentRole === 'TEACHER',
    isLeader: () => useAuthStore().currentRole === 'LEADER',
    // 账号是否拥有教师身份（看角色集合，不依赖当前激活视图）。
    // 用于“评分/编辑置灰提示”与“登录待评提醒”：多角色账号（如 ADMIN/LEADER 兼 TEACHER）
    // 也能命中，避免切到非教师视图时彻底看不到提示。
    hasTeacherRole: (state) => Array.isArray(state.roles) && state.roles.includes('TEACHER'),
    // 统一权限模型：判断当前用户是否拥有某模块某 action 权限。
    // 由 /api/user/me 回写的 permissions（module -> [action...]）驱动，不依赖硬编码角色码。
    can: (state) => (module, action) => {
      if (state.superAdmin) return true // 超级管理员恒有权限
      const actions = state.permissions && state.permissions[module]
      return Array.isArray(actions) && actions.includes(action)
    },
    // 听课评课「导出全成员评课表」按钮显隐：需拥有 observe:export_full。
    // 组长 owner 的数据级判定由后端 exportEvaluateZip 做，前端仅按权限点控制按钮可用态。
    canExportObserveFull: (state) => {
      if (state.superAdmin) return true
      const actions = state.permissions && state.permissions.observe
      return Array.isArray(actions) && actions.includes('export_full')
    },
    // 年段范围：教师 / 备课组长 / 教学组长 / 年段长 均受绑定年段约束（后端 applyDataScope / resolveScopeGradeIds 口径一致）；
    // 教师、备课组长、教学组长：仅限用户所隶属的年段（备课组长/教学组长再叠加本学科约束）；
    // 年段长仅收口年段，学科/学期不限制（可看全学科、全学期）；
    // 教务主任(ADMIN) 不限年段（返回空，前端即开放全部年段）。
    // 多角色用户切到教务主任视图时，年段下拉显示全部年段。
    myGradeIds: (state) => {
      const role = useAuthStore().currentRole
      if (role === 'TEACHER' || role === 'HEAD_PREP' || role === 'HEAD_TEACHING' || role === 'LEADER') {
        return state.rawGradeIds
      }
      return []
    }
  },
  actions: {
    // 重置数据范围缓存：登录/登出后必须调用，确保下次进入页面按新账号重新判定学科与年段
    reset() {
      this.loaded = false
      this.username = ''
      this.userId = null
      this.roles = []
      this.mySubjectId = null
      this.rawGradeIds = []
      this.superAdmin = false
    },
    async ensureLoaded() {
      if (this.loaded) return
      const data = await me()
      this.username = data.username || ''
      this.userId = data.id ?? null
      this.roles = Array.isArray(data.roles) ? data.roles : []
      this.mySubjectId = data.subjectId ?? null
      this.rawGradeIds = Array.isArray(data.gradeIds) ? data.gradeIds : []
      this.superAdmin = data.isSuperAdmin === true
      this.permissions = (data.permissions && typeof data.permissions === 'object') ? data.permissions : {}
      this.loaded = true
    }
  }
})
